import { Modal, notification } from 'antd';
import {
    createDestination,
    generateDestinationContent,
    translateDestinationContent,
} from 'api/destinations.api';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import { useDestinationEditor } from './DestinationEditorContext';

const { confirm } = Modal;

type ContentGenerationAction = 'generate' | 'translate';

const CONFIRM_COPY: Record<
    ContentGenerationAction,
    { title: string; content: string; okText: string }
> = {
    generate: {
        title: 'Generate content',
        content:
            'This will overwrite the existing content for this destination (including any unsaved changes) with AI-generated content. This cannot be undone. Do you want to continue?',
        okText: 'Generate',
    },
    translate: {
        title: 'Translate content',
        content:
            'This will overwrite the existing translations for this destination (including any unsaved changes) with AI-generated translations into Dutch, German, French and Spanish. This cannot be undone. Do you want to continue?',
        okText: 'Translate',
    },
};

export function useDestinationContentGeneration() {
    const {
        destination,
        location,
        isCountry,
        isEditing,
        hasContent,
        updateDestination,
        setLocale,
        handleUpdateContentSyncKey,
    } = useDestinationEditor();
    const navigate = useNavigate();

    const [isGenerating, setIsGenerating] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    const isBusy = isGenerating || isTranslating;

    const canGenerate =
        !isCountry && !!location && !!destination.name && !isBusy;
    const canTranslate = !isCountry && isEditing && hasContent && !isBusy;

    const handleApplyGeneratedDestination = useCallback(
        (updatedDestination: DestinationSchema, wasNewlyCreated: boolean) => {
            updateDestination(updatedDestination);
            // Signal list-based tabs (Highlights, Itinerary, Good to Know)
            // to re-sync their local state from the freshly generated /
            // translated content. Without this, they keep showing the
            // pre-generation contents and language switching appears to
            // do nothing until a full page refresh.
            handleUpdateContentSyncKey();
            mutate(`destination_by_id/${updatedDestination.id}`);
            if (wasNewlyCreated) {
                navigate(`/destinations/${updatedDestination.id}`, {
                    replace: true,
                });
            }
        },
        [updateDestination, handleUpdateContentSyncKey, navigate],
    );

    const handleGenerateContent = useCallback(async () => {
        setIsGenerating(true);

        // Known upfront: whether we'll need to create the destination first.
        // Declared outside the try block so the catch handler below can
        // still see it even if generation itself fails.
        const wasNewlyCreated = !destination.id;
        let destinationId = destination.id;

        try {
            if (!destinationId) {
                const created = await createDestination(destination);
                destinationId = created.id;
                // Persist the newly created destination locally too, so the
                // rest of the form (name, id, etc.) reflects reality even if
                // the generate call below fails.
                updateDestination(created);
            }

            const generated = await generateDestinationContent(destinationId);
            handleApplyGeneratedDestination(generated, wasNewlyCreated);
            setLocale('en');
            notification.success({
                message: 'Content generated successfully',
                key: 'destination-content-generated',
            });
        } catch (error) {
            // Content generation might have failed after the destination was
            // successfully created. Navigate to it anyway so it isn't
            // "lost" - it already exists in the backend, the editor should
            // reflect that even though generation itself didn't finish.
            if (wasNewlyCreated && destinationId) {
                navigate(`/destinations/${destinationId}`, { replace: true });
            }
            notification.error({
                message: 'Error generating content',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsGenerating(false);
        }
    }, [
        destination,
        updateDestination,
        handleApplyGeneratedDestination,
        setLocale,
        navigate,
    ]);

    const handleTranslateContent = useCallback(async () => {
        if (!destination.id) return;
        setIsTranslating(true);
        try {
            const translated = await translateDestinationContent(
                destination.id,
            );
            handleApplyGeneratedDestination(translated, false);
            notification.success({
                message: 'Content translated successfully',
                key: 'destination-content-translated',
            });
        } catch (error) {
            notification.error({
                message: 'Error translating content',
                description:
                    error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setIsTranslating(false);
        }
    }, [destination.id, handleApplyGeneratedDestination]);

    const handleConfirmAndRun = useCallback(
        (action: ContentGenerationAction, run: () => void) => {
            if (hasContent) {
                const copy = CONFIRM_COPY[action];
                confirm({
                    title: copy.title,
                    content: copy.content,
                    okText: copy.okText,
                    okButtonProps: { danger: true },
                    cancelText: 'Cancel',
                    onOk: run,
                });
            } else {
                run();
            }
        },
        [hasContent],
    );

    const handleGenerateClick = useCallback(() => {
        handleConfirmAndRun('generate', () => {
            handleGenerateContent();
        });
    }, [handleConfirmAndRun, handleGenerateContent]);

    const handleTranslateClick = useCallback(() => {
        handleConfirmAndRun('translate', () => {
            handleTranslateContent();
        });
    }, [handleConfirmAndRun, handleTranslateContent]);

    return {
        canGenerate,
        canTranslate,
        isGenerating,
        isTranslating,
        handleGenerateClick,
        handleTranslateClick,
    };
}
